import React from 'react';
import { AddNewNodeIcon, DeleteIcon, ColorPickerIcon, FocusIcon } from './icons';

function MenuItem({ icon, children, onClick, itemStyle, hoverBg, autoFocus = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      autoFocus={autoFocus}
      style={{
        ...itemStyle,
        width: '100%',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = hoverBg)}
      onMouseLeave={(event) => (event.currentTarget.style.backgroundColor = 'transparent')}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function MenuGroup({ label, children, text, border, first = false }) {
  if (!children) return null;
  return (
    <section
      aria-label={label}
      style={{
        borderTop: first ? 'none' : `1px solid ${border}`,
        marginTop: first ? 0 : 4,
        paddingTop: first ? 0 : 4,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          padding: '3px 10px 2px',
          color: text,
          opacity: 0.55,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      {children}
    </section>
  );
}

function TaskIcon({ text, checked = false }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        border: `1.5px solid ${text}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        flexShrink: 0,
      }}
    >
      {checked ? '✓' : '+'}
    </span>
  );
}

function NodeContextMenu({
  isDarkMode,
  onAddChild,
  onAddSibling,
  onAddTaskChild,
  onDelete,
  onChangeColor,
  onChangeFont,
  onFocusBranch,
  showFocusAction = false,
  onShareIdea,
  onOpenBranch,
  onCopyText,
  onCopyLink,
  isTask = false,
  onToggleTask,
  onChangeEmoji,
  onCreateMindmapLink,
  onCreateMarkdown,
  onConvertBranchToMarkdown,
  canConvertBranchToMarkdown = true,
  canCreateMindmapLink = true,
  canAddSibling = true,
  canWrite = true,
  canDiscuss = false,
  onOpenDiscussion,
  canOpenInfo = false,
  onOpenInfo,
  canModerate = false,
  isModerated = false,
  onHideBranch,
  onRestoreBranch,
  canAssignTask = false,
  taskAssignees = [],
  assignedPrincipal = null,
  assignmentLoading = false,
  assignmentError = null,
  onAssignTask,
}) {
  const bg = isDarkMode ? '#1f2933' : '#ffffff';
  const border = isDarkMode ? '#4b5563' : '#e5e7eb';
  const text = isDarkMode ? '#e5e7eb' : '#111827';
  const hoverBg = isDarkMode ? '#374151' : '#f3f4f6';
  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 13,
    color: text,
    borderRadius: 4,
    minHeight: 44,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  const menuItemProps = { itemStyle, hoverBg };
  const markdownIcon = (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 16,
        border: `1px solid ${text}`,
        borderRadius: 3,
        fontSize: 9,
        textTransform: 'lowercase',
        flexShrink: 0,
      }}
    >
      md
    </span>
  );

  return (
    <div
      role="menu"
      aria-label="Idea actions"
      style={{
        minWidth: 220,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
        padding: 6,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {canWrite && (
        <MenuGroup label="Create" text={text} border={border} first>
          <MenuItem
            {...menuItemProps}
            autoFocus={!canOpenInfo && !canDiscuss}
            onClick={onAddChild}
            icon={<AddNewNodeIcon />}
          >
            Add idea
          </MenuItem>
          {canAddSibling && (
            <MenuItem {...menuItemProps} onClick={onAddSibling} icon={<AddNewNodeIcon />}>
              Add sibling idea (Enter / Shift+Tab)
            </MenuItem>
          )}
          <MenuItem {...menuItemProps} onClick={onAddTaskChild} icon={<TaskIcon text={text} />}>
            Add task
          </MenuItem>
          <MenuItem {...menuItemProps} onClick={onCreateMarkdown} icon={markdownIcon}>
            Create Markdown
          </MenuItem>
          {canCreateMindmapLink && (
            <MenuItem
              {...menuItemProps}
              onClick={onCreateMindmapLink}
              icon={(
                <img
                  src="/favicon-32x32.png"
                  width="16"
                  height="16"
                  alt=""
                  aria-hidden="true"
                  style={{ display: 'block', borderRadius: 3, flexShrink: 0 }}
                />
              )}
            >
              New Map
            </MenuItem>
          )}
        </MenuGroup>
      )}

      <MenuGroup label="Idea" text={text} border={border} first={!canWrite}>
        {canOpenInfo && (
          <MenuItem {...menuItemProps} autoFocus onClick={onOpenInfo} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>ⓘ</span>}>
            Info
          </MenuItem>
        )}
        {canDiscuss && (
          <MenuItem {...menuItemProps} autoFocus={!canOpenInfo} onClick={onOpenDiscussion} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>💬</span>}>
            Discuss
          </MenuItem>
        )}
        {canWrite && canAssignTask && isTask && (
          <label
            style={{
              ...itemStyle,
              alignItems: 'stretch',
              cursor: assignmentLoading ? 'wait' : 'default',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden="true" style={{ fontSize: 16 }}>@</span>
              <span>Responsible</span>
            </span>
            <select
              aria-label="Assign task to collaborator"
              value={assignedPrincipal?.userId || ''}
              disabled={assignmentLoading}
              onChange={(event) => {
                event.stopPropagation();
                const assignee = taskAssignees.find(
                  (candidate) => candidate.userId === event.target.value
                ) || null;
                onAssignTask?.(assignee);
              }}
              onClick={(event) => event.stopPropagation()}
              style={{
                width: '100%',
                minHeight: 34,
                borderRadius: 6,
                border: `1px solid ${border}`,
                background: bg,
                color: text,
                padding: '4px 7px',
              }}
            >
              <option value="">Unassigned</option>
              {assignedPrincipal?.userId
                && !taskAssignees.some((candidate) => candidate.userId === assignedPrincipal.userId) && (
                  <option value={assignedPrincipal.userId} disabled>
                    {assignedPrincipal.displayName || 'Former collaborator'} (unavailable)
                  </option>
                )}
              {taskAssignees.map((assignee) => (
                <option key={assignee.userId} value={assignee.userId}>
                  {assignee.selectionLabel || assignee.displayName}
                </option>
              ))}
            </select>
            {assignmentError && (
              <span role="status" style={{ fontSize: 11, opacity: 0.75 }}>
                Could not refresh the editor list. Try again when online.
              </span>
            )}
          </label>
        )}
        {canWrite && (
          <MenuItem
            {...menuItemProps}
            onClick={onToggleTask}
            icon={<TaskIcon text={text} checked={isTask} />}
          >
            {isTask ? 'Remove task flag' : 'Convert to task'}
          </MenuItem>
        )}
        {canWrite && (
          <MenuItem {...menuItemProps} onClick={onChangeColor} icon={<ColorPickerIcon />}>
            Change color
          </MenuItem>
        )}
        {canWrite && onChangeFont && <MenuItem {...menuItemProps} onClick={onChangeFont} icon={<span aria-hidden="true">Aa</span>}>Font…</MenuItem>}
        {canWrite && (
          <MenuItem {...menuItemProps} onClick={onChangeEmoji} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>😊</span>}>
            Emoji
          </MenuItem>
        )}
        <MenuItem {...menuItemProps} onClick={onCopyText} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>⧉</span>}>
          Copy idea text
        </MenuItem>
      </MenuGroup>

      <MenuGroup label="Branch" text={text} border={border}>
        {showFocusAction && <MenuItem {...menuItemProps} onClick={onFocusBranch} icon={<FocusIcon />}>
          Focus on this branch
        </MenuItem>}
        <MenuItem {...menuItemProps} onClick={onShareIdea} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>↗</span>}>
          Share idea
        </MenuItem>
        <MenuItem {...menuItemProps} onClick={onOpenBranch} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>◰</span>}>
          Open branch in new tab
        </MenuItem>
        <MenuItem {...menuItemProps} onClick={onCopyLink} icon={<span aria-hidden="true" style={{ fontSize: 16 }}>🔗</span>}>
          Copy link to this branch
        </MenuItem>
        {canWrite && canConvertBranchToMarkdown && (
          <MenuItem {...menuItemProps} onClick={onConvertBranchToMarkdown} icon={<span aria-hidden="true" style={{ width: 18, textAlign: 'center', fontSize: 16 }}>≡</span>}>
            Turn branch into Markdown list
          </MenuItem>
        )}
      </MenuGroup>

      {(canModerate || canWrite) && (
        <MenuGroup label="Manage" text={text} border={border}>
          {canModerate && (
            <MenuItem
              {...menuItemProps}
              onClick={isModerated ? onRestoreBranch : onHideBranch}
              icon={<span aria-hidden="true" style={{ fontSize: 16 }}>{isModerated ? '↺' : '!'}</span>}
            >
              {isModerated ? 'Restore hidden branch' : 'Hide collaborator branch'}
            </MenuItem>
          )}
          {canWrite && (
            <MenuItem {...menuItemProps} onClick={onDelete} icon={<DeleteIcon />}>
              Delete idea
            </MenuItem>
          )}
        </MenuGroup>
      )}
    </div>
  );
}

export default NodeContextMenu;
